/**
 * The session autopsy page's data (command-center VERIFICATION_TOOLS
 * 2026-10-11, section 2.5), straight from pv-auth:
 *   GET /provider/admin/people/:id/sessions   one person's traces, newest first, filtered
 *   GET /provider/admin/sessions/:peerId      one trace with its entries and the engine lines when fetched
 *   GET /provider/admin/sessions/failed       every session that did not finish since yesterday
 * Shapes mirror src/services/sessionTraceAdmin.service.ts in pv-auth-service.
 *
 * Pure functions only below the fetchers, so the rules that put an entry on
 * the right lane, give it its one line, and colour a gate block or an abort
 * are testable without a DOM. Nothing here invents: a trace carries codes
 * (the ending, the reason, a clip id, a movement name), never the person's
 * words, because none are stored (section 2.4), and the page shows the codes.
 */

export const TRACE_KINDS = ['daily_check', 'onboarding', 'rom_screen', 'activation'] as const;
export const TRACE_ENDINGS = ['completed', 'aborted', 'error', 'timeout', 'left', 'socket_closed', 'unknown'] as const;
export const TRACE_PLATFORMS = ['web', 'desktop', 'ios', 'android'] as const;
export type TraceKind = (typeof TRACE_KINDS)[number];
export type TraceEnding = (typeof TRACE_ENDINGS)[number];
export type TracePlatform = (typeof TRACE_PLATFORMS)[number];
export type TraceSrc = 'engine' | 'client';

export interface TraceEntry {
  /** Milliseconds since the trace started, on the client's clock. */
  t: number;
  src: TraceSrc;
  kind: string;
  data?: Record<string, unknown> | null;
}

export interface TraceResultJoin { id: string; date: string; score: number | null; poise: number | null; posture: number | null }

export interface TraceSummary {
  id: string;
  userId: string;
  peerId: string;
  kind: TraceKind;
  engineSessionId: string | null;
  platform: TracePlatform;
  appVersion: string | null;
  engineBase: string | null;
  startedAt: string;
  endedAt: string | null;
  durationS: number | null;
  ending: TraceEnding;
  endingReason: string | null;
  entryCount: number;
  truncated: boolean;
  droppedCount: number;
  bytes: number;
  engineLinesFetched: boolean;
  result: TraceResultJoin | null;
  createdAt: string;
}

export interface TraceFeedRow extends TraceSummary { person: { userId: string; email: string; name: string } | null }

export interface TraceCloudWatch {
  url: string | null;
  environment: string | null;
  region: string | null;
  logGroups: string[];
  validUntil: string;
  expired: boolean;
  note: string;
}

export interface TraceDetail extends TraceSummary {
  entries: TraceEntry[];
  /** Null is "not fetched" (PR 8 fills it), never an empty list. */
  engineLines: unknown[] | null;
  engineLinesNote: string;
  cloudWatch: TraceCloudWatch;
  generatedAt: string;
}

export interface PersonSessions {
  userId: string;
  traces: TraceSummary[];
  limit: number;
  filters: { ending: TraceEnding | null; kind: TraceKind | null; platform: TracePlatform | null; from: string | null; to: string | null };
  generatedAt: string;
}

export interface FailedSessions { since: string; until: string; traces: TraceFeedRow[]; limit: number; generatedAt: string }

export interface SessionFilters { ending?: string; kind?: string; platform?: string; from?: string; to?: string; limit?: number }

export const sessionsQueryString = (f: SessionFilters): string => {
  const p = new URLSearchParams();
  if (f.ending) p.set('ending', f.ending);
  if (f.kind) p.set('kind', f.kind);
  if (f.platform) p.set('platform', f.platform);
  if (f.from) p.set('from', f.from);
  if (f.to) p.set('to', f.to);
  if (f.limit) p.set('limit', String(f.limit));
  return p.toString();
};

export async function fetchPersonSessions(userId: string, f: SessionFilters = {}): Promise<PersonSessions> {
  const { apiGet } = await import('@/services/base');
  const qs = sessionsQueryString(f);
  return (await apiGet(`/provider/admin/people/${encodeURIComponent(userId)}/sessions${qs ? `?${qs}` : ''}`, true)) as PersonSessions;
}

export async function fetchSessionTrace(userId: string, peerId: string): Promise<TraceDetail> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet(`/provider/admin/sessions/${encodeURIComponent(peerId)}?user=${encodeURIComponent(userId)}`, true)) as TraceDetail;
}

export async function fetchFailedSessions(since = '24h'): Promise<FailedSessions> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet(`/provider/admin/sessions/failed?since=${encodeURIComponent(since)}`, true)) as FailedSessions;
}

/* ── words ──────────────────────────────────────────────────────────────── */

export const TRACE_KIND_LABEL: Record<TraceKind, string> = {
  daily_check: 'Daily check',
  onboarding: 'Onboarding check',
  rom_screen: 'Movement screen',
  activation: 'Activation',
};

export const TRACE_ENDING_LABEL: Record<TraceEnding, string> = {
  completed: 'completed',
  aborted: 'aborted',
  error: 'ended in an error',
  timeout: 'timed out',
  left: 'left',
  socket_closed: 'lost its socket',
  unknown: 'ended without a known ending',
};

export const TRACE_PLATFORM_LABEL: Record<TracePlatform, string> = { web: 'web', desktop: 'desktop', ios: 'iOS', android: 'Android' };

export const traceKindLabel = (k: string): string => (TRACE_KIND_LABEL as Record<string, string>)[k] ?? k.replace(/_/g, ' ');
export const traceEndingLabel = (e: string): string => (TRACE_ENDING_LABEL as Record<string, string>)[e] ?? e.replace(/_/g, ' ');
export const tracePlatformLabel = (p: string): string => (TRACE_PLATFORM_LABEL as Record<string, string>)[p] ?? p;

/** The header line: "Movement screen, aborted: rom_neutral_timeout" or "Daily check, completed". */
export const endingLine = (t: Pick<TraceSummary, 'kind' | 'ending' | 'endingReason'>): string =>
  `${traceKindLabel(t.kind)}, ${traceEndingLabel(t.ending)}${t.endingReason ? `: ${t.endingReason}` : ''}`;

/** "2 min 14 s", "48 s", or the words for an ending the client never saw; never 0. */
export const durationLabel = (s: number | null | undefined): string => {
  if (typeof s !== 'number' || !Number.isFinite(s)) return 'ending not seen';
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return m > 0 ? `${m} min ${r} s` : `${r} s`;
};

/** A trace time in seconds with one decimal: "12.5 s", and "1:02.5" past a minute. */
export const tLabel = (ms: number): string => {
  if (!Number.isFinite(ms) || ms < 0) return 'time not recorded';
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = Math.floor(s / 60);
  const r = s - m * 60;
  return `${m}:${r.toFixed(1).padStart(4, '0')}`;
};

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
const deg = (v: unknown): string => (num(v) === null ? 'not measured' : `${Math.round(num(v) as number)} deg`);
const secs = (ms: unknown): string => (num(ms) === null ? 'not measured' : `${((num(ms) as number) / 1000).toFixed(2)} s`);
const words = (code: string | null): string => (code ? code.replace(/_/g, ' ') : 'not recorded');

/* ── one line per entry ─────────────────────────────────────────────────── */

export type EntryTone = 'neutral' | 'band' | 'gate' | 'abort' | 'ok' | 'clip' | 'headline';

/**
 * What one entry means, in one line, from the codes it carries. Unknown
 * kinds get the kind and their scalar data, so a new entry kind from a newer
 * client is still readable without a deploy here.
 */
export const entryLine = (e: TraceEntry): string => {
  const d = (e.data ?? {}) as Record<string, unknown>;
  switch (e.kind) {
    case 'results_ws_open': return 'results socket opened';
    case 'results_ws_closed': return `results socket closed${num(d.code) !== null ? ` (code ${d.code})` : ''}`;
    case 'peer_connected': return 'peer connected';
    case 'first_frame': return 'first frame received';
    case 'offer': return 'offer sent';
    case 'answer': return `answer received${str(d.codec) ? `, ${d.codec}` : ''}`;
    case 'ice': return `ICE ${words(str(d.state))}`;
    case 'relay': return d.relay === true ? 'relay available' : d.relay === false ? 'no relay' : 'relay not reported';
    case 'stall': return `frames stalled for ${secs(d.ms)}`;
    case 'session_id': return `engine session ${str(d.sid) ?? 'not recorded'}`;
    case 'session_id_changed': return `engine session changed to ${str(d.sid) ?? 'not recorded'}`;
    case 'rom_ack': {
      const seq = Array.isArray(d.sequence) ? (d.sequence as unknown[]).map((x) => words(str(x))).join(', ') : null;
      return `engine acknowledged ${str(d.cmd) ?? 'the command'}${str(d.mode) ? ` (${d.mode})` : ''}${seq ? `: ${seq}` : ''}`;
    }
    case 'rom_status': {
      const idx = num(d.rom_index);
      const total = num(d.rom_total);
      const where = idx !== null && total !== null ? ` (${idx + 1} of ${total})` : '';
      return `${words(str(d.stage))}, ${words(str(d.active_rom))}${where}${num(d.current_deg) !== null ? `, ${deg(d.current_deg)}` : ''}`;
    }
    case 'rom_stage_change': return `${words(str(d.rom))} to ${words(str(d.stage))}${d.baseline_locked === true ? ', baseline locked' : d.baseline_locked === false ? ', baseline not locked' : ''}`;
    case 'rom_neutral_timeout': return `${words(str(d.rom))}: neutral never locked${num(d.window_mean_deg) !== null ? ` (window mean ${deg(d.window_mean_deg)}, readable ${num(d.readable_frac) === null ? 'not measured' : `${Math.round((num(d.readable_frac) as number) * 100)}%`})` : ''}`;
    case 'rom_return_timeout': return `${words(str(d.rom))}: no return to neutral`;
    case 'rom_event_complete': {
      const s = (d.summary ?? {}) as Record<string, unknown>;
      const rom = words(str(d.rom) ?? str(s.rom_type));
      if (s.measured === false) return `${rom}: not measured (${str(s.not_measured_reason) ?? 'no reason given'})`;
      return `${rom}: ${deg(s.achieved_deg)}${num(s.target_deg) !== null ? ` of ${deg(s.target_deg)}` : ''}${str(s.classification) ? `, ${s.classification}` : ''}`;
    }
    case 'rom_session_complete': {
      const ev = Array.isArray(d.events) ? (d.events as Record<string, unknown>[]) : [];
      const measured = ev.filter((x) => x.measured === true).length;
      return `screen complete: ${measured} of ${ev.length} measured${num(d.composite_score) !== null ? `, composite ${d.composite_score}` : ', no composite'}`;
    }
    case 'rom_result': return `result: composite ${num(d.composite_score) === null ? 'not measured' : d.composite_score}`;
    case 'status': return `status ${words(str(d.status))}${str(d.from) ? ` (from ${words(str(d.from))})` : ''}`;
    case 'gate': {
      const block = str(d.block);
      const head = d.ok === true ? 'gate open' : block ? `gate blocked: ${words(block)}` : 'gate not open';
      const dims = [num(d.pitch_deg) !== null ? `pitch ${deg(d.pitch_deg)}` : null, num(d.distance_cm) !== null ? `distance ${Math.round(num(d.distance_cm) as number)} cm` : null, str(d.quality) ? `quality ${d.quality}` : null].filter(Boolean);
      return dims.length ? `${head} (${dims.join(', ')})` : head;
    }
    case 'lock': return `baseline locked${num(d.baseline_confidence) !== null ? `, confidence ${d.baseline_confidence}` : ''}${d.forced === true ? ', forced' : ''}`;
    case 'beat': return `beat ${words(str(d.beat))} ${words(str(d.beat_state))}${d.in_band === true ? ', in band' : d.in_band === false ? ', out of band' : ''}${str(d.gate_reason) ? `, ${words(str(d.gate_reason))}` : ''}`;
    case 'heartbeat': return `heartbeat: ${words(str(d.pose_state))}${num(d.stability_progress) !== null ? `, stability ${Math.round((num(d.stability_progress) as number) * 100)}%` : ''}`;
    case 'result': return `result: composite ${num(d.composite) === null ? 'not measured' : d.composite}${str(d.worst_row) ? `, worst row ${words(str(d.worst_row))}` : ''}${d.baseline_forced === true ? ', baseline forced' : ''}${d.ended_early === true ? ', ended early' : ''}`;
    case 'aborted': return `aborted: ${str(d.reason) ?? 'no reason given'}${d.retriable === true ? ' (retriable)' : ''}${str(d.block) ? `, block ${words(str(d.block))}` : ''}`;
    case 'error': return `engine error: ${str(d.code) ?? 'unknown'}`;
    case 'settle_ack': return `${words(str(d.action))} ${d.accepted === true ? 'accepted' : d.accepted === false ? `refused${str(d.reason) ? `: ${d.reason}` : ''}` : 'answered'}`;
    case 'event': return `engine event ${str(d.event) ?? 'unnamed'}`;
    case 'analytics_event': return `analytics event${str(d.completion_state) ? `: ${words(str(d.completion_state))}` : ''}`;
    case 'headline': return `headline: ${words(str(d.movement))}${d.holding === true ? ' (holding)' : ''}`;
    case 'gate_line': return `gate line shown: ${words(str(d.line_id))}`;
    case 'clip_start': return `clip ${str(d.clip) ?? 'unnamed'} started${num(d.expected_ms) !== null ? `, ${secs(d.expected_ms)} expected` : ''}`;
    case 'clip_end': return `clip ${str(d.clip) ?? 'unnamed'} ended after ${secs(d.ms)}${str(d.how) ? `, ${words(str(d.how))}` : ''}`;
    case 'chime': return `chime ${str(d.clip) ?? ''}${d.sounded === false ? ' (silent)' : ''}`.trim();
    case 'prompt_window': return `${words(str(d.movement))}: the client window ${d.open === true ? 'opened' : 'closed'}`;
    case 'screen': return `screen ${words(str(d.state))}${str(d.movement) ? `, ${words(str(d.movement))}` : ''}${str(d.reason) ? ` (${d.reason})` : ''}`;
    case 'button': return `pressed ${words(str(d.id))}${str(d.how) ? ` (${d.how})` : ''}`;
    case 'send': return `sent ${words(str(d.action))}${str(d.rom) ? ` ${words(str(d.rom))}` : ''}`;
    case 'skip': return `skipped ${words(str(d.rom))}`;
    case 'placement': return `camera band ${words(str(d.band))}${str(d.source) ? ` (${d.source})` : ''}`;
    case 'posthog': return `tracked ${str(d.event) ?? 'an event'}`;
    default: {
      const scalars = Object.entries(d).filter(([, v]) => v === null || ['string', 'number', 'boolean'].includes(typeof v)).map(([k, v]) => `${k} ${v === null ? 'not measured' : String(v)}`);
      return scalars.length ? `${words(e.kind)}: ${scalars.join(', ')}` : words(e.kind);
    }
  }
};

/** The colour an entry earns: gate blocks amber, aborts and anything not measured red, bands for stage changes, bars for clips. */
export const entryTone = (e: TraceEntry): EntryTone => {
  const d = (e.data ?? {}) as Record<string, unknown>;
  if (e.kind === 'aborted' || e.kind === 'error' || e.kind === 'rom_neutral_timeout' || e.kind === 'rom_return_timeout' || e.kind === 'stall') return 'abort';
  if (e.kind === 'rom_event_complete') return ((d.summary ?? {}) as Record<string, unknown>).measured === false ? 'abort' : 'ok';
  if (e.kind === 'gate') return d.ok === true ? 'ok' : 'gate';
  if (e.kind === 'rom_status' || e.kind === 'status') return 'band';
  if (e.kind === 'clip_start' || e.kind === 'clip_end') return 'clip';
  if (e.kind === 'headline') return 'headline';
  if (e.kind === 'result' || e.kind === 'rom_session_complete' || e.kind === 'rom_result' || e.kind === 'lock' || e.kind === 'first_frame') return 'ok';
  return 'neutral';
};

/* ── the two lanes on one clock ─────────────────────────────────────────── */

export interface ClipBar { clip: string; movement: string | null; startT: number; endT: number | null; ms: number | null; expectedMs: number | null; how: string | null }

/** Every clip as a bar: its start, and its measured end when the client reported one. A clip without an end was still playing when the trace ended. */
export const clipBars = (entries: TraceEntry[]): ClipBar[] => {
  const open = new Map<string, ClipBar>();
  const out: ClipBar[] = [];
  for (const e of [...entries].sort((a, b) => a.t - b.t)) {
    const d = (e.data ?? {}) as Record<string, unknown>;
    const clip = str(d.clip) ?? 'unnamed';
    if (e.kind === 'clip_start') {
      const bar: ClipBar = { clip, movement: str(d.movement), startT: e.t, endT: null, ms: null, expectedMs: num(d.expected_ms), how: null };
      open.set(clip, bar);
      out.push(bar);
    } else if (e.kind === 'clip_end') {
      const bar = open.get(clip);
      if (bar) { bar.endT = e.t; bar.ms = num(d.ms); bar.how = str(d.how); open.delete(clip); }
    }
  }
  return out;
};

/** The entries in time order, with a stable tie-break so engine and client rows at the same instant keep their posted order. */
export const inClockOrder = (entries: TraceEntry[]): TraceEntry[] => entries.map((e, i) => ({ e, i })).sort((a, b) => a.e.t - b.e.t || a.i - b.i).map((x) => x.e);

/** How many entries each lane holds, for the filter row. */
export const laneCounts = (entries: TraceEntry[]): Record<TraceSrc, number> => {
  const out: Record<TraceSrc, number> = { engine: 0, client: 0 };
  for (const e of entries) if (e.src === 'engine' || e.src === 'client') out[e.src] += 1;
  return out;
};

/**
 * Moments where the two lanes disagree: a clip for one movement still
 * playing while the engine has moved to another, which is the October 6
 * defect in one picture (design 2.9). Pure, from the clip bars and the
 * engine's rom_status entries.
 */
export interface Disagreement { fromT: number; toT: number; voice: string; engine: string }

export const disagreements = (entries: TraceEntry[]): Disagreement[] => {
  const bars = clipBars(entries).filter((b) => b.movement && b.endT !== null);
  const statuses = inClockOrder(entries).filter((e) => e.kind === 'rom_status' && str((e.data ?? {}).active_rom));
  const out: Disagreement[] = [];
  for (const b of bars) {
    // The first instant inside the clip where the engine names another movement; the disagreement runs from there to the clip's end.
    const moved = statuses.find((s) => s.t > b.startT && s.t < (b.endT as number) && str((s.data ?? {}).active_rom) !== b.movement);
    if (moved) out.push({ fromT: moved.t, toT: b.endT as number, voice: b.movement as string, engine: str((moved.data ?? {}).active_rom) as string });
  }
  return out;
};

/* ── the data under a row ───────────────────────────────────────────────── */

export interface DataChip { key: string; value: string; missing: boolean }

/** Scalars as chips in the order they arrived; nested values are JSON so nothing is rounded or dropped on the way to the screen. */
export const dataChips = (data: Record<string, unknown> | null | undefined): DataChip[] =>
  Object.entries(data ?? {}).map(([key, v]) => ({
    key,
    value: v === null || v === undefined ? 'not measured' : typeof v === 'object' ? JSON.stringify(v) : typeof v === 'boolean' ? (v ? 'yes' : 'no') : String(v),
    missing: v === null || v === undefined,
  }));

/* ── the guard the render test leans on ─────────────────────────────────── */

/** The keys the server refuses at the door (sessionTrace.service.ts). The page must never show them, because they must never arrive. */
export const BANNED_TRACE_KEYS = ['hns', 'body', 'poses', 'kpts', 'keypoints', 'upper_body_keypoints', 'landmarks', 'frame', 'frames', 'image', 'image_b64', 'jpeg', 'transcript', 'words', 'utterance'] as const;

const bannedSet = new Set<string>(BANNED_TRACE_KEYS);

/** The first banned key found anywhere in a value, or null. */
export const bannedKeyIn = (value: unknown, depth = 0): string | null => {
  if (depth > 8 || value === null || typeof value !== 'object') return null;
  if (Array.isArray(value)) {
    for (const v of value) { const hit = bannedKeyIn(v, depth + 1); if (hit) return hit; }
    return null;
  }
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (bannedSet.has(k.toLowerCase())) return k;
    const hit = bannedKeyIn(v, depth + 1);
    if (hit) return hit;
  }
  return null;
};

/** Newest first for the Today list, by the session start; the id breaks a tie so the order is stable. */
export const newestFirst = <T extends { startedAt: string; id: string }>(rows: T[]): T[] =>
  [...rows].sort((a, b) => (a.startedAt === b.startedAt ? (a.id < b.id ? 1 : -1) : a.startedAt < b.startedAt ? 1 : -1));
